import { useEffect, useRef, useState } from "react";
import { Crosshair, ExternalLink, MapPin, MapPinned, Save } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";
import ImageUpload from "@/components/ImageUpload";

const ST_RUPRECHT_CENTER = [47.1539, 15.6622];
const LOCAL_RADIUS_KM = 10;

function coordinate(value, axis) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  const limit = axis === "lat" ? 90 : 180;
  return Number.isFinite(parsed) && Math.abs(parsed) <= limit ? parsed : null;
}

function distanceFromStRuprecht(lat, lng) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const [centerLat, centerLng] = ST_RUPRECHT_CENTER;
  const deltaLat = toRadians(lat - centerLat);
  const deltaLng = toRadians(lng - centerLng);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(centerLat)) * Math.cos(toRadians(lat)) * Math.sin(deltaLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function addPickerMarker(map, lat, lng) {
  return L.circleMarker([lat, lng], {
    radius: 9,
    color: "#fff",
    fillColor: "#c4703a",
    fillOpacity: 1,
    weight: 3,
  }).addTo(map);
}

function StationLocation({ station, onSave }) {
  const [latitude, setLatitude] = useState(station.latitude ?? "");
  const [longitude, setLongitude] = useState(station.longitude ?? "");
  const [accuracy, setAccuracy] = useState(null);
  const [status, setStatus] = useState("idle");
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const parsedLatitude = coordinate(latitude, "lat");
  const parsedLongitude = coordinate(longitude, "lng");
  const hasValidCoordinates = parsedLatitude != null && parsedLongitude != null;
  const distanceKm = hasValidCoordinates ? distanceFromStRuprecht(parsedLatitude, parsedLongitude) : null;
  const outsideStRuprecht = distanceKm != null && distanceKm > LOCAL_RADIUS_KM;

  useEffect(() => {
    setLatitude(station.latitude ?? "");
    setLongitude(station.longitude ?? "");
  }, [station.latitude, station.longitude]);

  useEffect(() => {
    if (!pickerOpen || !pickerRef.current || mapRef.current) return undefined;

    const initialIsLocal = hasValidCoordinates && !outsideStRuprecht;
    const initial = initialIsLocal ? [parsedLatitude, parsedLongitude] : ST_RUPRECHT_CENTER;
    const map = L.map(pickerRef.current, { scrollWheelZoom: false }).setView(initial, initialIsLocal ? 17 : 15);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    if (initialIsLocal) markerRef.current = addPickerMarker(map, parsedLatitude, parsedLongitude);
    map.on("click", ({ latlng }) => {
      setLatitude(latlng.lat.toFixed(7));
      setLongitude(latlng.lng.toFixed(7));
      setAccuracy(null);
      setStatus("ready");
    });
    window.setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [pickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !hasValidCoordinates || outsideStRuprecht) return;
    if (markerRef.current) markerRef.current.setLatLng([parsedLatitude, parsedLongitude]);
    else markerRef.current = addPickerMarker(map, parsedLatitude, parsedLongitude);
  }, [hasValidCoordinates, outsideStRuprecht, parsedLatitude, parsedLongitude]);

  const capture = () => {
    if (!navigator.geolocation) {
      setStatus("unsupported");
      return;
    }
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLatitude(coords.latitude.toFixed(7));
        setLongitude(coords.longitude.toFixed(7));
        setAccuracy(Math.round(coords.accuracy));
        setStatus("ready");
      },
      () => setStatus("denied"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const save = async () => {
    if (!hasValidCoordinates) {
      setStatus("invalid");
      return;
    }
    if (outsideStRuprecht) {
      setStatus("outside");
      return;
    }
    setStatus("saving");
    const ok = await onSave(station, parsedLatitude.toFixed(7), parsedLongitude.toFixed(7));
    setStatus(ok ? "saved" : "ready");
  };

  return (
    <div className="mt-6 rounded-2xl border bg-[var(--brand-soft)] p-5">
      <p className="eyebrow">Position auf der interaktiven Karte</p>
      <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
        Tippe am iPad auf „Auf Karte auswählen“ und danach auf den exakten Ort. Du musst die Koordinaten nicht mehr aus Google Maps abschreiben.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-[var(--ink-soft)]">
          Breitengrad
          <input className="field mt-1" inputMode="decimal" value={latitude} onChange={(event) => setLatitude(event.target.value)} />
        </label>
        <label className="text-xs font-semibold text-[var(--ink-soft)]">
          Längengrad
          <input className="field mt-1" inputMode="decimal" value={longitude} onChange={(event) => setLongitude(event.target.value)} />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="btn btn-ghost !py-2.5 !text-xs" onClick={() => setPickerOpen((open) => !open)}>
          <MapPinned className="h-4 w-4" /> {pickerOpen ? "KARTE SCHLIESSEN" : "AUF KARTE AUSWÄHLEN"}
        </button>
        <button type="button" className="btn btn-ghost !py-2.5 !text-xs" onClick={capture} disabled={status === "loading"}>
          <Crosshair className="h-4 w-4" /> {status === "loading" ? "STANDORT WIRD GESUCHT …" : "AKTUELLEN STANDORT ÜBERNEHMEN"}
        </button>
        <button type="button" className="btn btn-primary !py-2.5 !text-xs" onClick={save} disabled={!hasValidCoordinates || outsideStRuprecht || status === "saving"}>
          <Save className="h-4 w-4" /> {status === "saving" ? "SPEICHERT …" : "POSITION SPEICHERN"}
        </button>
      </div>
      {pickerOpen ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold text-[var(--ink-soft)]">Karte verschieben, hineinzoomen und den richtigen Punkt antippen.</p>
          <div ref={pickerRef} className="h-72 overflow-hidden rounded-2xl border bg-[#d8dfd9]" />
        </div>
      ) : null}
      {accuracy != null ? <p className="mt-3 flex items-center gap-1.5 text-xs text-[var(--ink-soft)]"><MapPin className="h-3.5 w-3.5" /> GPS-Genauigkeit ungefähr ± {accuracy} m</p> : null}
      {hasValidCoordinates && !outsideStRuprecht ? (
        <a
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand)] underline"
          href={`https://www.google.com/maps/search/?api=1&query=${parsedLatitude},${parsedLongitude}`}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Position vor dem Speichern prüfen
        </a>
      ) : null}
      {outsideStRuprecht ? (
        <p className="mt-3 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700">
          Diese Position liegt rund {Math.round(distanceKm)} km von St. Ruprecht entfernt und wird nicht gespeichert. Bitte den Punkt direkt auf der Karte auswählen.
        </p>
      ) : null}
      {status === "invalid" ? <p className="mt-3 text-xs text-red-700">Bitte beide Koordinaten als Dezimalzahlen eingeben oder den Punkt auf der Karte auswählen.</p> : null}
      {status === "outside" ? <p className="mt-3 text-xs text-red-700">Die Position liegt außerhalb von St. Ruprecht.</p> : null}
      {status === "denied" ? <p className="mt-3 text-xs text-red-700">Standortzugriff wurde nicht erlaubt. Du kannst die Werte auch manuell eintragen.</p> : null}
      {status === "unsupported" ? <p className="mt-3 text-xs text-red-700">Dieses Gerät unterstützt keine Standortbestimmung.</p> : null}
      {status === "saved" ? <p className="mt-3 text-xs font-semibold text-[var(--brand)]">Position gespeichert und öffentlich übernommen.</p> : null}
    </div>
  );
}

export default function PilotImages() {
  const { community, slug } = useCommunity();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    if (!slug) return;
    try {
      const { data: walks } = await api.get(`/communities/${slug}/walks`);
      const spuren = walks.find((w) => w.slug === "spuren") || walks[0];
      if (!spuren) throw new Error("Kein SPUREN-Weg vorhanden.");
      const { data: detail } = await api.get(`/walks/${spuren.id}`);
      setData(detail);
      setError("");
    } catch (e) {
      setError(errMsg(e, "SPUREN konnte nicht geladen werden."));
    }
  };

  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCommunityImage = async (value) => {
    if (!community?.id) return;
    try {
      await api.put(`/admin/communities/${community.id}`, { hero_image: value });
      toast.success("Startseitenbild gespeichert. Nach dem Neuladen ist es öffentlich sichtbar.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveWalkImage = async (value) => {
    try {
      await api.put(`/admin/walks/${data.walk.id}`, { hero_image: value });
      setData((d) => ({ ...d, walk: { ...d.walk, hero_image: value } }));
      toast.success("Wegbild gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveStationImage = async (station, value) => {
    try {
      await api.put(`/admin/stations/${station.id}`, { hero_image: value });
      const incoming = (data.segments || []).filter((s) => s.to_station_id === station.id);
      await Promise.all(incoming.map((s) => api.put(`/admin/walk_segments/${s.id}`, { destination_image: value })));
      setData((d) => ({
        ...d,
        stations: d.stations.map((s) => s.id === station.id ? { ...s, hero_image: value } : s),
        segments: d.segments.map((s) => s.to_station_id === station.id ? { ...s, destination_image: value } : s),
      }));
      toast.success("Stationsbild gespeichert und als Zielbild übernommen.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveStationField = async (station, field, value, message) => {
    try {
      await api.put(`/admin/stations/${station.id}`, { [field]: value });
      setData((d) => ({
        ...d,
        stations: d.stations.map((s) => s.id === station.id ? { ...s, [field]: value } : s),
      }));
      toast.success(message);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveStationLocation = async (station, latitude, longitude) => {
    try {
      await api.put(`/admin/stations/${station.id}`, { latitude, longitude });
      setData((d) => ({
        ...d,
        stations: d.stations.map((s) => s.id === station.id ? { ...s, latitude, longitude } : s),
      }));
      toast.success("Stationsposition gespeichert.");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const saveSegmentImage = async (segment, field, value) => {
    try {
      await api.put(`/admin/walk_segments/${segment.id}`, { [field]: value });
      setData((d) => ({
        ...d,
        segments: d.segments.map((s) => s.id === segment.id ? { ...s, [field]: value } : s),
      }));
      toast.success(field === "route_image" ? "Wegkarte gespeichert." : "Zielbild gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (error) return <div className="rounded-2xl border bg-white p-6 text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-[var(--ink-soft)]">Pilotbilder werden geladen …</div>;

  return (
    <div className="max-w-4xl">
      <p className="eyebrow">SPUREN · Pilot</p>
      <h1 className="mt-1 text-4xl">Echte Bilder & Wegkarten</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--ink-soft)]">
        Hier ersetzt du die alten Demo- und Stockbilder durch echte Aufnahmen aus St. Ruprecht. Stationsbilder werden automatisch auch als „Hier musst du hin“-Zielbild verwendet. Die Wegkarten lädst du darunter je Wegabschnitt hoch.
      </p>

      <section className="mt-8 rounded-3xl border bg-white p-6">
        <ImageUpload
          value={community?.hero_image}
          onChange={saveCommunityImage}
          label="Startseite St. Ruprecht – echtes Herobild"
          testId="pilot-community-image"
        />
      </section>

      <section className="mt-4 rounded-3xl border bg-white p-6">
        <ImageUpload value={data.walk.hero_image} onChange={saveWalkImage} label="Start-/Wegbild SPUREN" testId="pilot-walk-image" />
      </section>

      <div className="mt-6 space-y-4">
        {data.stations.map((station, index) => (
          <section key={station.id} className="rounded-3xl border bg-white p-6">
            <p className="eyebrow">SPUR {String(index + 1).padStart(2, "0")}</p>
            <h2 className="mt-1 text-2xl">{station.title}</h2>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">{station.location_description}</p>
            <div className="mt-5">
              <ImageUpload
                value={station.hero_image}
                onChange={(value) => saveStationImage(station, value)}
                label={station.title === "Kleine Brücke" ? "Echtes Stationsfoto – Wegentscheidung (Bild folgt)" : station.title === "Der Kasten" ? "Echtes Stationsfoto – geschlossenen Kasten verwenden" : "Echtes Stationsfoto"}
                testId={`pilot-station-${station.id}`}
              />
            </div>
            <StationLocation station={station} onSave={saveStationLocation} />
            {station.title === "Ortskern" ? (
              <div className="mt-6 rounded-2xl border bg-[var(--brand-soft)] p-5">
                <p className="eyebrow">Damals · Heute</p>
                <h3 className="mt-1 text-2xl">Historischer Vergleich</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                  Nur diese Station erhält den Bildschieber. Verwende zwei möglichst ähnlich aufgenommene Ansichten desselben Ortes.
                </p>
                <div className="mt-5 grid gap-6 md:grid-cols-2">
                  <ImageUpload
                    value={station.comparison_before_image}
                    onChange={(value) => saveStationField(station, "comparison_before_image", value, "Historisches Bild gespeichert.")}
                    label="Damals – historisches Bild"
                    testId={`pilot-comparison-before-${station.id}`}
                  />
                  <ImageUpload
                    value={station.comparison_after_image || station.hero_image}
                    onChange={(value) => saveStationField(station, "comparison_after_image", value, "Heutiges Vergleichsbild gespeichert.")}
                    label="Heute – möglichst gleicher Blickwinkel"
                    testId={`pilot-comparison-after-${station.id}`}
                  />
                </div>
              </div>
            ) : null}
          </section>
        ))}
      </div>

      <div className="mt-10">
        <p className="eyebrow">Wegkarten</p>
        <h2 className="mt-1 text-3xl">Von Station zu Station</h2>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">
          Hier kommen deine Karten/Screenshots hinein. Bei der Brücke bleiben beide Varianten getrennt.
        </p>
        <div className="mt-5 space-y-4">
          {data.segments.map((segment) => (
            <section key={segment.id} className="rounded-3xl border bg-white p-6">
              <h3 className="text-xl">{segment.title}</h3>
              {segment.variant_name ? <p className="mt-1 text-xs font-bold uppercase tracking-wide">{segment.variant_name}</p> : null}
              <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">{segment.instruction}</p>
              <div className="mt-5 grid gap-6 md:grid-cols-2">
                <ImageUpload
                  value={segment.route_image}
                  onChange={(value) => saveSegmentImage(segment, "route_image", value)}
                  label="Wegkarte / Wegbild"
                  testId={`pilot-route-${segment.id}`}
                />
                <ImageUpload
                  value={segment.destination_image}
                  onChange={(value) => saveSegmentImage(segment, "destination_image", value)}
                  label="Hier musst du hin"
                  testId={`pilot-destination-${segment.id}`}
                />
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
